import React from 'react';
import { botMessageIsDropdown } from '../helpers';
import {
    AdaptiveButtonList,
    AdaptiveButtonListData,
    Dropdown,
    DropdownAttachmentData,
    GroupSelectAttachmentData,
    NewGroupMultiSelect,
    NewGroupSingleSelect,
    MSHeroAttachment,
    MSHeroAttachmentData,
    ProvidenceButtonList,
    ProvidenceButtonListData,
} from '.';
import { Activity, AdaptiveCardAttachmentData, Attachment } from '../types';

import { AmpFlagSet } from '../amplitude';

const isGroupMultiSelect: (
    attachmentContent: GroupSelectAttachmentData
) => boolean = (attachmentContent) =>
    !!attachmentContent.body &&
    !!attachmentContent.body.length &&
    !!attachmentContent.body[0].items &&
    !!attachmentContent.body[0].items.length &&
    attachmentContent.body[0].items[0].isMultiSelect;

const isGroupSingleSelect: (
    attachmentContent: GroupSelectAttachmentData
) => boolean = (attachmentContent) =>
    !!attachmentContent.body &&
    !!attachmentContent.body.length &&
    !!attachmentContent.body[0].items &&
    !!attachmentContent.body[0].items.length &&
    !attachmentContent.body[0].items[0].isMultiSelect;

type AnchoredElementContainerArgs = {
    activity: Activity;
    featureFlags: AmpFlagSet;
    sendMessage: (message: string) => void;
    sendPostBack: (message: string) => void;
};

type AnchoredElementArgs = {
    attachment: Attachment;
    featureFlags: AmpFlagSet;
    messageText: string;
    sendMessage: (message: string) => void;
    sendPostBack: (message: string) => void;
};

export const AnchoredElementContainer: React.FC<
    AnchoredElementContainerArgs
> = ({
    activity,
    featureFlags,
    sendMessage,
    sendPostBack,
}: AnchoredElementContainerArgs) => {
    const anchoredElements = activity?.attachments?.map(
        (attachment: Attachment, attachmentIdx: number) => (
            <AnchoredElement
                attachment={attachment}
                key={`anchored-element-${attachmentIdx}`}
                featureFlags={featureFlags}
                messageText={activity?.text || ''}
                sendMessage={sendMessage}
                sendPostBack={sendPostBack}
            />
        )
    );
    return <div>{anchoredElements}</div>;
};

export const AnchoredElement: React.FC<AnchoredElementArgs> = ({
    attachment,
    featureFlags,
    messageText,
    sendMessage,
    sendPostBack,
}: AnchoredElementArgs) => {
    const { contentType, content } = attachment;
    switch (contentType) {
        case 'application/vnd.microsoft.card.adaptive': {
            switch ((content as AdaptiveCardAttachmentData).type) {
                case 'AdaptiveCard': {
                    if (botMessageIsDropdown(content as DropdownAttachmentData))
                        return (
                            <Dropdown
                                data={content as DropdownAttachmentData}
                                disableButtons={false}
                                featureFlags={featureFlags}
                                messageText={messageText}
                                sendPostBack={sendPostBack}
                            />
                        );

                    if (
                        isGroupMultiSelect(content as GroupSelectAttachmentData)
                    )
                        return (
                            <NewGroupMultiSelect
                                data={content as GroupSelectAttachmentData}
                                disableButtons={false}
                                sendMessage={sendMessage}
                                sendPostBack={sendPostBack}
                            />
                        );

                    if (
                        isGroupSingleSelect(
                            content as GroupSelectAttachmentData
                        )
                    )
                        return (
                            <NewGroupSingleSelect
                                data={content as GroupSelectAttachmentData}
                                disableButtons={false}
                                sendMessage={sendMessage}
                            />
                        );

                    return (
                        <AdaptiveButtonList
                            data={content as AdaptiveButtonListData}
                            disableButtons={false}
                            featureFlags={featureFlags}
                            sendMessage={sendMessage}
                        />
                    );
                }

                default: {
                    return <pre>{JSON.stringify(attachment, null, 2)}</pre>;
                }
            }
        }

        case 'application/providence.buttonlist': {
            return (
                <ProvidenceButtonList
                    data={content as ProvidenceButtonListData}
                    disableButtons={false}
                    featureFlags={featureFlags}
                    sendMessage={sendMessage}
                    sendPostBack={sendPostBack}
                />
            );
        }

        case 'application/vnd.microsoft.card.hero': {
            return (
                <MSHeroAttachment
                    data={content as MSHeroAttachmentData}
                    disableButtons={false}
                    featureFlags={featureFlags}
                    sendMessage={sendMessage}
                />
            );
        }

        default: {
            return null;
        }
    }
};
